/**
 * AudioPlayButton — request a TTS narration, then play it inline.
 *
 * Compact button suitable for embedding in a card header. Clicking once
 * fetches/generates the clip and starts playback. A small player UI
 * (progress bar + pause/play) appears beneath the trigger after the audio
 * loads. Signed URLs live 1 hour — past that, refresh via /v1/audio/:id/url.
 */
import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { api } from '@/lib/api';

interface GeneratedClip {
  id: string;
  signedUrl: string;
  durationSecondsEstimate: number;
  cached: boolean;
  voice: string;
  charCount: number;
}

interface Props {
  /** Endpoint to POST to (e.g. '/v1/audio/briefing' or '/v1/audio/recap/<id>') */
  endpoint: string;
  /** Visible label shown on the trigger button. */
  label?: string;
  /** Optional voice override; defaults to alloy. */
  voice?: string;
  /** Extra classes for the wrapper. */
  className?: string;
}

export function AudioPlayButton({ endpoint, label = 'Listen', voice, className }: Props) {
  const [clip, setClip] = useState<GeneratedClip | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0-1
  const audioRef = useRef<HTMLAudioElement | null>(null);

  async function generateAndPlay() {
    setError(null);
    if (clip) {
      // Toggle play/pause on existing clip
      const a = audioRef.current;
      if (!a) return;
      if (a.paused) { a.play().catch(e => setError(String(e))); }
      else { a.pause(); }
      return;
    }
    setLoading(true);
    try {
      const body = voice ? JSON.stringify({ voice }) : JSON.stringify({});
      const res = await api<{ ok: boolean; clip: GeneratedClip; error?: string; message?: string; hint?: string }>(
        endpoint, { method: 'POST', body },
      );
      if (!res.ok || !res.clip) {
        throw new Error(res.message ?? res.hint ?? res.error ?? 'Audio generation failed');
      }
      setClip(res.clip);
      // Autoplay after first generate — give the <audio> a tick to mount.
      setTimeout(() => audioRef.current?.play().catch(e => setError(String(e))), 50);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Wire up audio element events when clip arrives
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setProgress(0); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
    };
  }, [clip]);

  return (
    <div className={clsx('inline-flex flex-col items-stretch gap-1.5', className)}>
      <button
        onClick={generateAndPlay}
        disabled={loading}
        title={error ?? (clip ? `Voice: ${clip.voice}${clip.cached ? ' (cached)' : ''}` : 'Generate audio narration')}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-2xs font-medium transition-all',
          'border-border/60 hover:bg-surface-2 disabled:opacity-50',
          playing && 'bg-primary/10 border-primary/40 text-primary',
        )}
      >
        {loading ? (
          <>
            <svg aria-hidden="true" className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="32" strokeDashoffset="8" />
            </svg>
            Generating…
          </>
        ) : playing ? (
          <>
            <svg aria-hidden="true" className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="2" width="3" height="12"/><rect x="10" y="2" width="3" height="12"/></svg>
            Pause
          </>
        ) : clip ? (
          <>
            <svg aria-hidden="true" className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2v12l10-6z"/></svg>
            Resume
          </>
        ) : (
          <>
            <svg aria-hidden="true" className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1a3 3 0 0 0-3 3v4a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M3 7v1a5 5 0 0 0 4 4.9V14H5v1h6v-1H9v-1.1A5 5 0 0 0 13 8V7h-1v1a4 4 0 1 1-8 0V7H3z"/>
            </svg>
            {label}
          </>
        )}
      </button>

      {error && (
        <div className="text-2xs text-danger max-w-[200px]">{error}</div>
      )}

      {clip && (
        <>
          {/* Progress bar */}
          <div className="relative h-1 rounded-full bg-surface-2 overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-primary transition-[width] duration-100"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <audio ref={audioRef} src={clip.signedUrl} preload="auto" className="hidden" />
        </>
      )}
    </div>
  );
}
